
import { supabase } from "@/integrations/supabase/client";

export interface InterviewSubmission {
  requestLink: string;
  answer: string;
  name: string;
  title?: string;
  company?: string;
  photographerCredit?: string;
  photoUrl?: string;
  contextUrl?: string;
}

export class InterviewSubmissionService {
  /**
   * Submit an interview response
   */
  static async submitInterviewResponse(submissionData: InterviewSubmission): Promise<{success: boolean, error?: string}> {
    try {
      console.log(`Submitting interview response for link: ${submissionData.requestLink}`);
      console.log(`Submission data:`, {
        requestLink: submissionData.requestLink,
        answerLength: submissionData.answer?.length || 0,
        name: submissionData.name,
        title: submissionData.title || '',
        company: submissionData.company || '',
        photoUrl: submissionData.photoUrl || '',
        hasPhotoUrl: !!submissionData.photoUrl
      });
      
      // Resolve the interview request via secure RPC to avoid direct table reads
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'find_request_by_link',
        { search_link: submissionData.requestLink.trim() }
      );
      const request = Array.isArray(rpcData) ? rpcData[0] : rpcData;
      const requestError = rpcError;
      
      console.log('Database lookup result:', { request, requestError });

      if (requestError) {
        console.error('Error fetching interview request for submission:', requestError);
        return { success: false, error: 'Failed to find the interview request: ' + requestError.message };
      }

      if (!request) {
        console.error('Interview request not found for link:', submissionData.requestLink);
        return { success: false, error: 'Interview request not found' };
      }

      // Check if it's already completed
      if (request.status === 'completed') {
        console.log('Interview already submitted:', request);
        return { success: false, error: 'This interview has already been submitted' };
      }

      // Check if it's expired
      if (request.status === 'expired' || (request.expires_at && new Date(request.expires_at) < new Date())) {
        console.log('Interview request expired:', request);
        return { success: false, error: 'This interview request has expired' };
      }

      // Prepare parameters for the edge function - ensure all fields are properly set
      const functionParams = {
        requestLink: submissionData.requestLink,
        answer: submissionData.answer,
        name: submissionData.name,
        title: submissionData.title || '',
        company: submissionData.company || '',
        photographerCredit: submissionData.photographerCredit || '',
        photoUrl: submissionData.photoUrl || '',
        contextUrl: submissionData.contextUrl || ''
      };
      
      console.log('Using Edge Function to submit interview response with params:', {
        requestLink: functionParams.requestLink,
        answerPreview: functionParams.answer.length > 50 ? `${functionParams.answer.substring(0, 50)}...` : functionParams.answer,
        name: functionParams.name,
        title: functionParams.title,
        company: functionParams.company,
        photographerCredit: functionParams.photographerCredit,
        photoUrl: functionParams.photoUrl,
        photoUrlLength: functionParams.photoUrl ? functionParams.photoUrl.length : 0,
        contextUrl: functionParams.contextUrl
      });
      
      // Use an Edge Function to bypass RLS and save the interview
      try {
        const { data: functionResponse, error: functionError } = await supabase.functions.invoke('save-interview-response', {
          body: functionParams
        });

        if (functionError) {
          console.error('Error from save-interview-response function:', functionError);
          return { success: false, error: 'Failed to save interview response: ' + functionError.message };
        }

        console.log('Response from Edge Function:', functionResponse);

        if (!functionResponse) {
          console.error('No response received from Edge Function');
          return { success: false, error: 'No response received from server' };
        }

        if (!functionResponse.success) {
          const errorMessage = functionResponse.error || 'Unknown error occurred';
          console.error('Error response from save-interview-response:', errorMessage);
          return { success: false, error: errorMessage };
        }

        console.log('Interview response submitted successfully:', functionResponse);
        return { success: true };
      } catch (functionCallError) {
        console.error('Exception calling Edge Function:', functionCallError);
        return { success: false, error: functionCallError instanceof Error ? functionCallError.message : 'Error calling submission service' };
      }
    } catch (error) {
      console.error('Error submitting interview response:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  }
}
